/**
 * Enhanced Garage Tool Detail View
 * Comprehensive tool detail page with metrics, sidebar metadata, and enhanced tabs
 */

import React, { useState, useEffect } from 'react';
import {
  ExternalLink, Star, Send, Users, TrendingUp, Clock,
  Bookmark, Share2, Check, X, Calendar, GitCompare, Smartphone
} from 'lucide-react';
import garageAPI from '../../../services/garage/api';
import GarageLoadingScreen from '../components/GarageLoadingScreen';
import AlertModal from '../modals/AlertModal';
import ShareToolModal from '../modals/ShareToolModal';
import { isBotApp, hasDynamicBotLaunch, toolHasWebUrl, toolHasRcAppUrl, launchToolAtUrl, getToolTypeBadgeSuffix } from '../../../utils/toolAppSubtype';

const formatLastUsedAt = (value) => {
  if (value === null) return 'Not used yet';
  if (!value) return 'Unavailable';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unavailable';
  return date.toLocaleDateString() === new Date().toLocaleDateString()
    ? 'Today'
    : date.toLocaleDateString();
};

const GarageToolDetailEnhanced = ({
  toolId,
  userEmail,
  userName,
  onNavigate,
  onNavigateToMaker,
  onToolLaunch,
  onRefreshPinnedTools, // Callback to refresh sidebar pinned tools
  onOpenRelatedTool // Opens another tool's detail view within the same Discover session
}) => {
  const [tool, setTool] = useState(null);
  const [toolRefreshVersion, setToolRefreshVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewFormData, setReviewFormData] = useState({ rating: 0, text: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [pinBusy, setPinBusy] = useState(false);
  const [relatedTools, setRelatedTools] = useState([]);
  const [toolStats, setToolStats] = useState(null);

  // Modal states
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    variant: 'info'
  });
  const [shareModalOpen, setShareModalOpen] = useState(false);

  useEffect(() => {
    if (toolId) {
      loadReviews();
      loadRelatedTools();
      loadToolStats();
    }
  }, [toolId]);

  useEffect(() => {
    let cancelled = false;
    setTool(null);
    if (!toolId) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    garageAPI.getTool(toolId).then((data) => {
      if (!cancelled) setTool(data);
    }).catch((error) => {
      console.error('Failed to load tool:', error);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [toolId, userEmail, toolRefreshVersion]);

  useEffect(() => {
    if (!toolId || !userEmail) {
      setIsPinned(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const userPins = await garageAPI.getUserPins(userEmail);
        if (!cancelled) {
          setIsPinned(userPins.some((pin) => String(pin.toolId) === String(toolId)));
        }
      } catch (error) {
        console.error('Failed to load bookmark state:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [toolId, userEmail]);

  const loadReviews = async () => {
    try {
      setReviewsLoading(true);
      const data = await garageAPI.getReviews(toolId);
      setReviews(data);
    } catch (error) {
      console.error('Failed to load reviews:', error);
      setReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  };

  const loadRelatedTools = async () => {
    try {
      const allTools = await garageAPI.getTools();
      // Filter related tools (same tags or similar)
      const related = allTools
        .filter(t => t.id !== toolId && t.status === 'published')
        .slice(0, 6);
      setRelatedTools(related);
    } catch (error) {
      console.error('Failed to load related tools:', error);
    }
  };

  const loadToolStats = async () => {
    try {
      const stats = await garageAPI.getToolStats(toolId);
      setToolStats(stats);
    } catch (error) {
      console.error('Failed to load tool stats:', error);
      // Set default values if stats fail to load
      setToolStats({
        sessions7d: 0,
        sessionsTotal: 0,
        uniqueUsers7d: 0,
        uniqueUsersTotal: 0,
        rating: 0,
        ratingCount: 0
      });
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (reviewFormData.rating === 0) {
      setAlertModal({
        isOpen: true,
        title: 'Validation Error',
        message: 'Please select a rating',
        variant: 'error'
      });
      return;
    }

    try {
      setSubmittingReview(true);
      await garageAPI.createReview({
        toolId: toolId,
        userEmail: userEmail,
        userName: userName || userEmail?.split('@')[0],
        rating: reviewFormData.rating,
        text: reviewFormData.text
      });

      setReviewFormData({ rating: 0, text: '' });
      setShowReviewForm(false);
      await loadReviews();
      setAlertModal({
        isOpen: true,
        title: 'Success',
        message: 'Review submitted successfully!',
        variant: 'success'
      });
    } catch (error) {
      console.error('Failed to submit review:', error);
      setAlertModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to submit review. Please try again.',
        variant: 'error'
      });
    } finally {
      setSubmittingReview(false);
    }
  };

  const handlePinTool = async () => {
    if (pinBusy || !userEmail) return;
    const nextPinned = !isPinned;
    try {
      setPinBusy(true);
      if (isPinned) {
        await garageAPI.removePin(toolId, userEmail);
      } else {
        await garageAPI.addPin({ toolId, userEmail });
      }
      setIsPinned(nextPinned);
      if (onRefreshPinnedTools) {
        onRefreshPinnedTools();
      }
    } catch (error) {
      console.error('Failed to toggle bookmark:', error);
      setAlertModal({
        isOpen: true,
        title: 'Bookmark failed',
        message: error?.response?.data?.detail || 'Could not update this bookmark. Please try again.',
        variant: 'error'
      });
    } finally {
      setPinBusy(false);
    }
  };

  const handleShare = () => {
    // Open share modal with user search, deep link, copy, and email options
    setShareModalOpen(true);
  };

  const handleLaunchTool = async (urlOverride, target) => {
    if (!tool) return;
    const targetUrl = (urlOverride || tool.url || '').trim();
    if (!targetUrl && !hasDynamicBotLaunch(tool)) return;
    await launchToolAtUrl(tool, targetUrl, { garageAPI, userEmail, onToolLaunch, target });
    setToolRefreshVersion((version) => version + 1);
  };

  const getAverageRating = () => {
    if (reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, r) => acc + r.rating, 0);
    return (sum / reviews.length).toFixed(1);
  };

  const getRatingDistribution = () => {
    const dist = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach(r => dist[r.rating]++);
    return dist;
  };

  const getPercentageForRating = (rating) => {
    const dist = getRatingDistribution();
    const total = reviews.length;
    if (total === 0) return 0;
    return Math.round((dist[rating] / total) * 100);
  };

  if (loading) {
    return (
      <div className="garage-content">
        <GarageLoadingScreen message="Loading tool..." />
      </div>
    );
  }

  if (!tool) {
    return (
      <div className="garage-content">
        <div className="garage-empty">
          <h3>Tool not found</h3>
          <button className="garage-btn garage-btn-primary" onClick={() => onNavigate('discover')}>
            Back to Discover
          </button>
        </div>
      </div>
    );
  }

  const avgRating = getAverageRating();
  // Get session metrics from tool stats
  const totalSessions = toolStats?.sessionsTotal || 0;
  const weeklyActive = toolStats?.uniqueUsers7d || 0;

  return (
    <div className="garage-content garage-tool-detail-enhanced">
      <div className="garage-tool-enhanced-container">
        {/* Main Content Area */}
        <div className="garage-tool-enhanced-main">
          {/* Hero Section */}
          <div className="garage-tool-enhanced-hero">
            <div className="garage-tool-enhanced-hero-left">
              {tool.imageUrl && (
                <div className="garage-tool-enhanced-icon">
                  <img src={tool.imageUrl} alt={tool.title} />
                </div>
              )}
              <div className="garage-tool-enhanced-header-info">
                <h1 className="garage-tool-enhanced-title">
                  {tool.title}
                  {tool.makers && tool.makers.some(m => m.isVerified) && (
                    <span className="garage-verified-badge-inline">✓</span>
                  )}
                </h1>
                <div className="garage-tool-enhanced-meta">
                  <span className="garage-tool-enhanced-type">
                    {tool.toolType === 'App' ? `App · ${getToolTypeBadgeSuffix(tool)}` : tool.toolType}
                  </span>
                  {tool.personas && tool.personas.length > 0 && (
                    <>
                      <span className="garage-tool-enhanced-separator">·</span>
                      <span>{tool.personas.join(', ')}</span>
                    </>
                  )}
                  {tool.environment && (
                    <>
                      <span className="garage-tool-enhanced-separator">·</span>
                      <span>{tool.environment}</span>
                    </>
                  )}
                </div>
                <p className="garage-tool-enhanced-description">{tool.description}</p>
              </div>
            </div>
          </div>

          {/* Metrics Bar */}
          <div className="garage-tool-enhanced-metrics">
            <div className="garage-tool-enhanced-metric">
              <Star size={16} fill="#FF7A00" color="#FF7A00" />
              <strong>{avgRating}</strong>
              <span>{reviews.length} ratings</span>
            </div>
            <span className="garage-tool-enhanced-metric-sep">·</span>
            <div className="garage-tool-enhanced-metric">
              <Users size={16} />
              <strong>{totalSessions.toLocaleString()}</strong>
              <span>total sessions</span>
            </div>
            <span className="garage-tool-enhanced-metric-sep">·</span>
            <div className="garage-tool-enhanced-metric">
              <TrendingUp size={16} />
              <strong>{weeklyActive}</strong>
              <span>/ 7d</span>
            </div>
            <span className="garage-tool-enhanced-metric-sep">·</span>
            <div className="garage-tool-enhanced-metric">
              <Clock size={16} />
              <span>Launched {new Date(tool.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="garage-tool-detail-tabs">
            <button
              className={`garage-tool-detail-tab ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button
              className={`garage-tool-detail-tab ${activeTab === 'how-to' ? 'active' : ''}`}
              onClick={() => setActiveTab('how-to')}
            >
              How to use
            </button>
            <button
              className={`garage-tool-detail-tab ${activeTab === 'changelog' ? 'active' : ''}`}
              onClick={() => setActiveTab('changelog')}
            >
              Changelog
            </button>
            <button
              className={`garage-tool-detail-tab ${activeTab === 'feedback' ? 'active' : ''}`}
              onClick={() => setActiveTab('feedback')}
            >
              Feedback <span className="garage-tab-badge">{reviews.length}</span>
            </button>
            <button
              className={`garage-tool-detail-tab ${activeTab === 'related' ? 'active' : ''}`}
              onClick={() => setActiveTab('related')}
            >
              Related
            </button>
          </div>

          {/* Tab Content */}
          <div className={`garage-tool-detail-content ${activeTab === 'related' ? 'is-related' : ''}`}>
            {activeTab === 'overview' && (
              <div>
                {/* Live Preview */}
                {tool.previewUrl && (
                  <div className="garage-live-preview-section">
                    <div className="garage-live-preview-label">LIVE PREVIEW</div>
                    <div className="garage-live-preview-frame">
                      <div className="garage-preview-toolbar">
                        <div className="garage-preview-dot" style={{ background: '#FF5F57' }}></div>
                        <div className="garage-preview-dot" style={{ background: '#FEBC2E' }}></div>
                        <div className="garage-preview-dot" style={{ background: '#28C840' }}></div>
                        <div className="garage-preview-url">ringo.ringcentral.com/apps/{tool.id}</div>
                      </div>
                      <div className="garage-preview-body">
                        <div className="garage-preview-title">{tool.title} in action</div>
                        <div className="garage-preview-text">{tool.description}</div>
                        <div className="garage-preview-loading">
                          &gt; {tool.title} ready.<br />
                          &gt; Loading workspace...<br />
                          &gt; Ready for input.
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* What It Does */}
                {tool.whatItDoes && tool.whatItDoes.length > 0 && (
                  <div className="garage-what-it-does-section">
                    <h3>WHAT IT DOES</h3>
                    <div className="garage-feature-list">
                      {tool.whatItDoes.map((feature, idx) => (
                        <div key={idx} className="garage-feature-item">
                          <div className="garage-feature-bullet"></div>
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* How-to Steps (bots) */}
                {Array.isArray(tool.steps) && tool.steps.length > 0 && (
                  <div className="garage-what-it-does-section">
                    <h3>STEPS</h3>
                    <ol className="garage-tool-steps-list">
                      {tool.steps.map((step, idx) => (
                        <li key={idx}>{step}</li>
                      ))}
                    </ol>
                  </div>
                )}

                {/* When to Use This */}
                {(tool.goodFitScenarios?.length > 0 || tool.notGoodFitScenarios?.length > 0) && (
                  <div>
                    <h3 style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.8px', color: 'var(--garage-text-3)', margin: '0 0 16px 0', textTransform: 'uppercase' }}>WHEN TO USE THIS</h3>
                    <div className="garage-fit-section">
                      {tool.goodFitScenarios && tool.goodFitScenarios.length > 0 && (
                        <div className="garage-fit-card">
                          <div className="garage-fit-card-header">
                            <div className="garage-fit-icon good">
                              <Check size={14} />
                            </div>
                            <div className="garage-fit-title">Good fit</div>
                          </div>
                          <div className="garage-fit-scenarios">
                            {tool.goodFitScenarios.map((scenario, idx) => (
                              <div key={idx} className="garage-fit-scenario">{scenario}</div>
                            ))}
                          </div>
                        </div>
                      )}

                      {tool.notGoodFitScenarios && tool.notGoodFitScenarios.length > 0 && (
                        <div className="garage-fit-card">
                          <div className="garage-fit-card-header">
                            <div className="garage-fit-icon not-good">
                              <X size={14} />
                            </div>
                            <div className="garage-fit-title">Not the right tool</div>
                          </div>
                          <div className="garage-fit-scenarios">
                            {tool.notGoodFitScenarios.map((scenario, idx) => (
                              <div key={idx} className="garage-fit-scenario">{scenario}</div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'how-to' && (
              <div className="garage-placeholder-content">
                <p className="garage-placeholder-text">How-to guide coming soon...</p>
              </div>
            )}

            {activeTab === 'changelog' && (
              <div className="garage-placeholder-content">
                <p className="garage-placeholder-text">Changelog coming soon...</p>
              </div>
            )}

            {activeTab === 'feedback' && (
              <div className="garage-feedback-section">
                {reviews.length > 0 && (
                  <div className="garage-rating-distribution">
                    <div className="garage-rating-dist-header">
                      <div className="garage-rating-dist-score">
                        <div className="garage-rating-dist-number">{avgRating}</div>
                        <div className="garage-rating-dist-stars">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              size={16}
                              fill={star <= Math.round(avgRating) ? '#FF7A00' : 'none'}
                              color={star <= Math.round(avgRating) ? '#FF7A00' : '#D0D0D0'}
                            />
                          ))}
                        </div>
                        <div className="garage-rating-dist-count">{reviews.length} ratings</div>
                      </div>
                      <div className="garage-rating-dist-bars">
                        {[5, 4, 3, 2, 1].map((rating) => {
                          const dist = getRatingDistribution();
                          const count = dist[rating] || 0;
                          const percentage = getPercentageForRating(rating);
                          return (
                            <div key={rating} className="garage-rating-dist-row">
                              <span className="garage-rating-dist-label">{rating}</span>
                              <Star size={12} fill="#FF7A00" color="#FF7A00" />
                              <div className="garage-rating-dist-bar-container">
                                <div
                                  className="garage-rating-dist-bar-fill"
                                  style={{ width: `${percentage}%` }}
                                ></div>
                              </div>
                              <span className="garage-rating-dist-percent">{count}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* Write Review Button & Form */}
                <div className="garage-review-submit-section">
                  {!showReviewForm ? (
                    <button
                      className="garage-btn garage-btn-primary"
                      onClick={() => setShowReviewForm(true)}
                    >
                      <Star size={16} />
                      Write a Review
                    </button>
                  ) : (
                    <div className="garage-review-form-card">
                      <h3 className="garage-review-form-title">Share Your Experience</h3>
                      <form onSubmit={handleSubmitReview}>
                        <div className="garage-review-form-rating">
                          <label>Your Rating *</label>
                          <div className="garage-review-stars-input">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <button
                                key={star}
                                type="button"
                                className="garage-star-button"
                                onClick={() => setReviewFormData({ ...reviewFormData, rating: star })}
                              >
                                <Star
                                  size={28}
                                  fill={star <= reviewFormData.rating ? '#FF7A00' : 'none'}
                                  color={star <= reviewFormData.rating ? '#FF7A00' : '#D0D0D0'}
                                />
                              </button>
                            ))}
                            <span className="garage-rating-label">
                              {reviewFormData.rating > 0 && (
                                reviewFormData.rating === 5 ? 'Excellent!' :
                                reviewFormData.rating === 4 ? 'Good' :
                                reviewFormData.rating === 3 ? 'Average' :
                                reviewFormData.rating === 2 ? 'Below Average' : 'Poor'
                              )}
                            </span>
                          </div>
                        </div>
                        <div className="garage-review-form-text">
                          <label>Your Review (optional)</label>
                          <textarea
                            rows="4"
                            value={reviewFormData.text}
                            onChange={(e) => setReviewFormData({ ...reviewFormData, text: e.target.value })}
                            placeholder="Share your thoughts about this tool..."
                            className="garage-review-textarea"
                          />
                        </div>
                        <div className="garage-review-form-actions">
                          <button
                            type="button"
                            className="garage-btn garage-btn-secondary"
                            onClick={() => {
                              setShowReviewForm(false);
                              setReviewFormData({ rating: 0, text: '' });
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="garage-btn garage-btn-primary"
                            disabled={submittingReview || reviewFormData.rating === 0}
                          >
                            {submittingReview ? 'Submitting...' : 'Submit Review'}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                </div>

                {/* Reviews List */}
                <div className="garage-reviews-list">
                  <h3 className="garage-reviews-list-title">All Reviews</h3>
                  {reviews.length === 0 ? (
                    <div className="garage-no-reviews">
                      <p>No reviews yet. Be the first to review this tool!</p>
                    </div>
                  ) : (
                    reviews.map((review) => (
                      <div key={review.id} className="garage-review-item">
                        <div className="garage-review-header">
                          <div className="garage-review-author">
                            <div className="garage-review-avatar">
                              {review.userName?.charAt(0)?.toUpperCase() || 'U'}
                            </div>
                            <div className="garage-review-author-info">
                              <strong>{review.userName || review.userEmail}</strong>
                              <span className="garage-review-date">
                                {new Date(review.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <div className="garage-review-rating">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                size={14}
                                fill={star <= review.rating ? '#FF7A00' : 'none'}
                                color={star <= review.rating ? '#FF7A00' : '#D0D0D0'}
                              />
                            ))}
                          </div>
                        </div>
                        {review.text && (
                          <p className="garage-review-text">{review.text}</p>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {activeTab === 'related' && (
              <div className="garage-related-section">
                <p className="garage-related-subtitle">
                  Tools that solve adjacent problems or work well alongside this one.
                </p>
                <div className="garage-related-tools-list">
                  {relatedTools.map((relTool) => (
                    <div
                      key={relTool.id}
                      className="garage-related-tool-card"
                      onClick={() => {
                        if (onOpenRelatedTool) {
                          onOpenRelatedTool(relTool.id);
                        }
                      }}
                    >
                      <div className="garage-related-tool-icon-wrapper">
                        {relTool.imageUrl ? (
                          <img src={relTool.imageUrl} alt={relTool.title} className="garage-related-tool-icon" />
                        ) : (
                          <div className="garage-related-tool-icon-placeholder">
                            {relTool.toolType === 'Gem' ? '💎' : '🔧'}
                          </div>
                        )}
                      </div>
                      <div className="garage-related-tool-content">
                        <h4 className="garage-related-tool-title">{relTool.title}</h4>
                        <p className="garage-related-tool-description">{relTool.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="garage-tool-enhanced-sidebar">
          {/* Action Buttons */}
          {isBotApp(tool) ? (
            <>
              {toolHasWebUrl(tool) && (
                <button
                  className="garage-btn garage-btn-primary garage-btn-launch"
                  onClick={() => handleLaunchTool(tool.url, 'web')}
                >
                  <ExternalLink size={16} />
                  Web
                </button>
              )}
              {toolHasRcAppUrl(tool) && (
                <button
                  className="garage-btn garage-btn-primary garage-btn-launch"
                  onClick={() => handleLaunchTool(tool.rcAppUrl, 'rc_app')}
                  style={{ marginTop: toolHasWebUrl(tool) ? '8px' : 0 }}
                >
                  <Smartphone size={16} />
                  RC app
                </button>
              )}
              {hasDynamicBotLaunch(tool)
                && !toolHasWebUrl(tool)
                && !toolHasRcAppUrl(tool) && (
                <div className="garage-tool-launch-unavailable" role="status">
                  This bot's user-specific conversation is not configured yet.
                </div>
              )}
            </>
          ) : (
            toolHasWebUrl(tool) && (
              <button
                className="garage-btn garage-btn-primary garage-btn-launch"
                onClick={() => handleLaunchTool()}
              >
                <ExternalLink size={16} />
                Launch
              </button>
            )
          )}

          <div className="garage-tool-enhanced-actions">
            <button
              className={`garage-btn garage-btn-secondary ${isPinned ? 'pinned' : ''}`}
              onClick={handlePinTool}
              disabled={pinBusy}
              title={isPinned ? 'Remove bookmark' : 'Bookmark this tool'}
            >
              <Bookmark size={16} fill={isPinned ? 'currentColor' : 'none'} />
              {isPinned ? 'Bookmarked' : 'Bookmark'}
            </button>
            {/* Compare button */}
            <button
              className="garage-btn garage-btn-secondary"
              onClick={() => {
                if (onNavigate) {
                  onNavigate('compare', { initialToolIds: [tool.id] });
                }
              }}
              title="Compare with other tools"
            >
              <GitCompare size={16} />
              Compare
            </button>
            <button
              className="garage-btn garage-btn-icon"
              onClick={handleShare}
              title="Share"
            >
              <Share2 size={16} />
            </button>
          </div>

          {/* BUILT BY Section */}
          {tool.makers && tool.makers.length > 0 && (
            <div className="garage-tool-sidebar-section">
              <h3>BUILT BY</h3>
              {tool.makers.map((maker, idx) => (
                <div
                  key={idx}
                  className="garage-tool-maker-info-static"
                >
                  <div className="garage-tool-maker-avatar">
                    {(maker.displayName || maker.makerEmail).charAt(0).toUpperCase()}
                  </div>
                  <div className="garage-tool-maker-info">
                    <div className="garage-tool-maker-name">{maker.displayName || maker.makerEmail}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* DETAILS Section */}
          <div className="garage-tool-sidebar-section">
            <h3>DETAILS</h3>
            <div className="garage-tool-details-grid">
              <div className="garage-tool-detail-row">
                <span className="garage-tool-detail-label">Type</span>
                <span className="garage-tool-detail-value">{tool.toolType}</span>
              </div>
              {tool.environment && (
                <div className="garage-tool-detail-row">
                  <span className="garage-tool-detail-label">Environment</span>
                  <span className="garage-tool-detail-value">{tool.environment}</span>
                </div>
              )}
              <div className="garage-tool-detail-row">
                <span className="garage-tool-detail-label">Model</span>
                <span className="garage-tool-detail-value">{tool.model || 'N/A'}</span>
              </div>
              {tool.updatedAt && (
                <div className="garage-tool-detail-row">
                  <span className="garage-tool-detail-label">Last updated</span>
                  <span className="garage-tool-detail-value">
                    {new Date(tool.updatedAt).toLocaleDateString() === new Date().toLocaleDateString() ? 'today' : new Date(tool.updatedAt).toLocaleDateString()}
                  </span>
                </div>
              )}
              <div className="garage-tool-detail-row">
                <span className="garage-tool-detail-label">You last used</span>
                <span className="garage-tool-detail-value">{formatLastUsedAt(tool.lastUsedAt)}</span>
              </div>
            </div>
          </div>

          {/* FOR Section */}
          {tool.personas && tool.personas.length > 0 && (
            <div className="garage-tool-sidebar-section">
              <h3>FOR</h3>
              <div className="garage-tool-sidebar-chips">
                {tool.personas.map((persona, idx) => (
                  <div key={idx} className="garage-tool-sidebar-chip">{persona}</div>
                ))}
              </div>
            </div>
          )}

          {/* SALES STAGES Section */}
          {tool.salesStages && tool.salesStages.length > 0 && (
            <div className="garage-tool-sidebar-section">
              <h3>SALES STAGES</h3>
              <div className="garage-tool-sidebar-chips">
                {tool.salesStages.map((stage, idx) => (
                  <div key={idx} className="garage-sales-stage-chip">{stage}</div>
                ))}
              </div>
            </div>
          )}

          {/* INTEGRATIONS Section */}
          {tool.integrations && tool.integrations.length > 0 && (
            <div className="garage-tool-sidebar-section">
              <h3>INTEGRATIONS</h3>
              <div className="garage-tool-sidebar-chips">
                {tool.integrations.map((integration, idx) => (
                  <div key={idx} className="garage-integration-badge">{integration}</div>
                ))}
              </div>
            </div>
          )}

          {/* SOCIAL PROOF Section */}
          {tool.socialLinks && Object.keys(tool.socialLinks).length > 0 && (
            <div className="garage-tool-sidebar-section">
              <h3>SOCIAL PROOF</h3>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                {tool.socialLinks.twitter && (
                  <a href={tool.socialLinks.twitter} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--garage-text-2)' }}>
                    <ExternalLink size={20} />
                  </a>
                )}
                {tool.socialLinks.linkedin && (
                  <a href={tool.socialLinks.linkedin} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--garage-text-2)' }}>
                    <ExternalLink size={20} />
                  </a>
                )}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--garage-text)' }}>
                <strong>{weeklyActive}</strong> people on your team use this weekly.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />

      {/* Share Tool Modal */}
      <ShareToolModal
        isOpen={shareModalOpen}
        tool={tool}
        userEmail={userEmail}
        onClose={() => setShareModalOpen(false)}
      />
    </div>
  );
};

export default GarageToolDetailEnhanced;
